/**
 * ScatterChart — tier-2 adapter.
 *
 * Scatter is an all-pairs form: any two marks can end up neighbours, so it
 * carries the documented series cap rather than the adjacent-pairs one.
 */
import { useMemo } from 'react';
import { ALL_PAIRS_SERIES_CAP } from '@/tokens/chart';
import { Chart, type ChartBaseProps } from './Chart';
import type { ChartConfig } from './types';

export interface ScatterSeries {
  /** Field name holding this series' measure. */
  key: string;
  name: string;
  color?: string;
}

export interface ScatterChartProps extends ChartBaseProps {
  xKey: string;
  series: readonly ScatterSeries[];
  title?: string;
}

export function ScatterChart({ xKey, series, title, ...rest }: ScatterChartProps) {
  if (process.env.NODE_ENV !== 'production' && series.length > ALL_PAIRS_SERIES_CAP) {
    console.warn(
      `[octopus-ui/chart] ScatterChart received ${series.length} series, above the ` +
        `all-pairs cap of ${ALL_PAIRS_SERIES_CAP}. Any two marks can be neighbours in a ` +
        'scatter plot, and no palette keeps more than that many hues mutually ' +
        'distinguishable under colour-vision simulation. Fold the tail into "Other" or ' +
        'facet into small multiples.',
    );
  }

  const config: ChartConfig = useMemo(
    () => ({
      title,
      chartType: 'scatter',
      series: series.map((s) => ({
        id: s.key,
        name: s.name,
        color: s.color,
        fields: { x: xKey, y: s.key },
      })),
    }),
    [xKey, series, title],
  );
  return <Chart config={config} {...rest} />;
}
